import { useState } from "react";
import { Link } from "react-router-dom";
import AuthLayout, { formatApiError } from "@/components/AuthLayout";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await api.post("/auth/forgot-password", { email: email.trim() });
      // Intentionally always show the same success — no email enumeration.
      setDone(true);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <AuthLayout
        title="Check your inbox."
        subtitle="If there's a Willoah account with that email, we've sent a link to reset the password. The link expires in an hour."
        footer={
          <>
            Remembered it?{" "}
            <Link to="/login" className="text-[#4E5A3D] underline underline-offset-2">Sign in</Link>
          </>
        }
      >
        <p className="text-sm text-[#2A2823]/70 leading-relaxed" data-testid="forgot-done">
          Didn't get anything? Check your spam folder — or try again in a minute if it still hasn't arrived.
        </p>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title="Forgot your password?"
      subtitle="No stress. Enter your email and we'll send you a link to set a new one."
      footer={
        <>
          Nevermind —{" "}
          <Link to="/login" className="text-[#4E5A3D] underline underline-offset-2">back to sign in</Link>
        </>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-4" data-testid="forgot-form">
        <div>
          <label className="text-xs uppercase tracking-wider text-[#2A2823]/60" htmlFor="forgot-email">Email</label>
          <input
            id="forgot-email"
            data-testid="forgot-email-input"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            autoComplete="email"
            className="mt-1 w-full rounded-full border border-[#B79256]/40 bg-white px-4 py-2.5 text-[15px] focus:outline-none focus:ring-2 focus:ring-[#4E5A3D]/30"
            required
          />
        </div>
        {error && (
          <div
            data-testid="forgot-error"
            className="text-sm text-[#8a3a2a] bg-[#8a3a2a]/10 border border-[#8a3a2a]/25 rounded-2xl px-4 py-2.5"
          >
            {error}
          </div>
        )}
        <Button
          type="submit"
          data-testid="forgot-submit"
          disabled={submitting || !email}
          className="w-full rounded-full bg-[#4E5A3D] text-[#FBF8F1] hover:bg-[#3E4830] h-12 text-base shadow-none disabled:opacity-60"
        >
          {submitting ? "Sending link…" : "Send reset link"}
        </Button>
      </form>
    </AuthLayout>
  );
}
